import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../../utility/Utility";
import RoleModel from "../RoleModel/RoleModel";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";
import IpAddressModal from "../../../Modal/IpAddressModal/IpAddressModal";

export default function AddRole() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  
  const [showUserModal, setShowUserModal] = useState(false);
  const [showIpModal, setShowIpModal] = useState(false);
  const [currentPermission, setCurrentPermission] = useState(null);
  const [formData, setFormData] = useState({
    userId: "",
    roleType: "Admin",
    permissionName: "",
    actions: [],
    allowedIPs: [],
  });

  // Updated permission enum with correct names
  const permissionEnum = [
    'admindashboard',
    'userManagement',
    'manageAccountDeletionRequest',
    'roleManagement',
    'websitesHomePageInquiry',
    'websitesInquiryFrom',
    'websitesAgentMeetingInquiry',
    'websitesAgentPhysicalInquiry',
    'CategoryManagement',
    'SubCategoryManagement',
    'EmployeeManagement',
    'CSVLeadManagement',
    'HeroSliderManagement',
    'TestimonialsManagement',
    'PartnersManagement',
    'CommissionManagement',
    'PolicyManagement',
    'FeedbackManagement',
    'RewardPointManagement',
    'VoucherManagement',
    'HelpAndSupportManagement',
    'SettingsManagement'
  ];

  const permissionDisplayNames = {
    admindashboard: "Dashboard",
    userManagement: "User Management",
    manageAccountDeletionRequest: "Manage Account Deletion Request",
    roleManagement: "Role Management",
    websitesHomePageInquiry: "Website Home Page Inquiry",
    websitesInquiryFrom: "Website Inquiry Form",
    websitesAgentMeetingInquiry: "Website Agent Meeting Inquiry",
    websitesAgentPhysicalInquiry: "Website Agent Physical Inquiry",
    CategoryManagement: "Category Management",
    SubCategoryManagement: "Sub Category Management",
    EmployeeManagement: "Employee Management",
    CSVLeadManagement: "CSV Lead Management",
    HeroSliderManagement: "Hero Slider Management",
    TestimonialsManagement: "Testimonials Management",
    PartnersManagement: "Partners Management",
    CommissionManagement: "Commission Management",
    PolicyManagement: "Policy Management",
    FeedbackManagement: "Feedback Management",
    RewardPointManagement: "Reward Point Management",
    VoucherManagement: "Voucher Management",
    HelpAndSupportManagement: "Help and Support Management",
    SettingsManagement: "Settings Management",
  };

  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState(
    permissionEnum.map(permission => ({
      permissionName: permission,
      actions: [],
      allowedIPs: []
    }))
  );

  console.log("permissions", permissions);

  // Initialize form with data from location if available (for update)
  useEffect(() => {
    if (state?.employee) {
      const employee = state.employee;
      setFormData(prev => ({
        ...prev,
        userId: employee._id,
        selectedUser: employee
      }));
    }

    // If editing existing permissions, populate the form
    if (state) {
      
      
      setFormData(prev => ({
        ...prev,
        userId: state.userId?._id || state.userId,
        roleType: state.roleType || "Admin",
        selectedUser: state.userId
      }));

      // Populate permissions from existing data
      if (state.permission && Array.isArray(state.permission)) {
        const updatedPermissions = permissionEnum.map(permission => {
          const existingPermission = state.permission.find(
            p => p.permissionName === permission
          );
          return {
            permissionName: permission,
            actions: existingPermission?.actions || [],
            allowedIPs: existingPermission?.allowedIPs || []
          };
        });
        setPermissions(updatedPermissions);
        console.log("Populated permissions:", updatedPermissions);
      }
    }
  }, [state]);

  const handleUserSelect = () => {
    setShowUserModal(true);
  }

  const handleIpSelect = (permissionName) => {
    setCurrentPermission(permissionName);
    setShowIpModal(true);
  }

  const handleSelectIps = (selectedIp) => {
    console.log("Selected IP:", selectedIp);
    
    if (currentPermission && selectedIp) {
      setPermissions(prevPermissions =>
        prevPermissions.map(permission => {
          if (permission.permissionName === currentPermission) {
            const newIpAddress = selectedIp.ipAddress;
            
            // Check if IP already exists
            if (permission.allowedIPs.includes(newIpAddress)) {
              toast.error(`IP address ${newIpAddress} is already selected for this permission`);
              return permission;
            }
            
            // Add new IP to the array
            return {
              ...permission,
              allowedIPs: [...permission.allowedIPs, newIpAddress]
            };
          }
          return permission;
        })
      );
    }
    setShowIpModal(false);
    setCurrentPermission(null);
  }

  // Function to remove an IP address from a permission
  const removeIpAddress = (permissionName, ipToRemove) => {
    setPermissions(prevPermissions =>
      prevPermissions.map(permission =>
        permission.permissionName === permissionName
          ? {
              ...permission,
              allowedIPs: permission.allowedIPs.filter(ip => ip !== ipToRemove)
            }
          : permission
      )
    );
  };

  const updatePermissionActions = (permissionName, action) => {
    setPermissions(prevPermissions => 
      prevPermissions.map(permission => 
        permission.permissionName === permissionName 
          ? {
              ...permission,
              actions: permission.actions.includes(action)
                ? permission.actions.filter(a => a !== action)
                : [...permission.actions, action]
            }
          : permission
      )
    );
  };

  const validateIP = (ip) => {
    if (ip === "*") return true; // Allow wildcard
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
    if (!ipRegex.test(ip)) return false;
    
    return ip.split('.').every(segment => {
      const num = parseInt(segment, 10);
      return num >= 0 && num <= 255;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.userId) {
      toast.error("Please select a user");
      return;
    }

    // Validate IP addresses
    const invalidIPs = [];
    permissions.forEach(permission => {
      permission.allowedIPs.forEach(ip => {
        if (ip && ip.trim() !== "" && !validateIP(ip)) {
          invalidIPs.push(`${ip} in ${permissionDisplayNames[permission.permissionName]}`);
        }
      });
    });

    if (invalidIPs.length > 0) {
      toast.error(`Invalid IP addresses: ${invalidIPs.join(', ')}`);
      return;
    }

    setLoading(true);

    try {
      // Filter out permissions with no actions
      const validPermissions = permissions
        .filter(permission => permission.actions.length > 0)
        .map(permission => ({
          permissionName: permission.permissionName,
          actions: permission.actions,
          allowedIPs: permission.allowedIPs.filter(ip => ip.trim() !== "")
        }));

      if (validPermissions.length === 0) {
        toast.error("Please select at least one action for permissions");
        setLoading(false);
        return;
      }

      // Prepare payload for update
      const payload = {
        userId: formData.userId,
        permission: validPermissions,
        roleType: formData.roleType,
      };



      const response = await postData("api/permissions", payload);
      console.log("API response:", response);

      if (response?.success) {
        toast.success(response?.message);
        navigate("/role-list");
      } else {
        toast.error(response?.message || (state ? "Failed to update permissions" : "Failed to assign permissions"));
      }
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || (state ? "Error updating permissions" : "Error assigning permissions"));
      } else if (error.request) {
        toast.error("Network error. Please try again.");
      } else {
        toast.error("An error occurred. Please try again.");
      }
      console.error("Add/Update role error:", error);
    } finally {
      setLoading(false);
    }
  };

  const hasActionsSelected = (permissionName) => {
    const permission = permissions.find(p => p.permissionName === permissionName);
    return permission && permission.actions.length > 0;
  };

  const getIpDisplayText = (allowedIPs) => {
    const nonEmptyIPs = allowedIPs.filter(ip => ip.trim() !== "");
    if (nonEmptyIPs.length === 0) return "No IPs selected";
    return `${nonEmptyIPs.length} IP(s) selected`;
  };

  // Check if we're in edit mode
  const isEditMode = !!state;

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <ToastContainer  />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1 mb-2 mb-sm-0">
              <h4 className="fs-18 fw-semibold m-0">{isEditMode ? "Edit Role" : "Add Role"}</h4>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Role Details</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-lg-6 col-md-12 mb-3">
                        <div className="d-flex flex-column flex-md-row align-items-md-center">
                          <label htmlFor="selectedRole" className="form-label mb-2 mb-md-0 me-md-3">
                            Select User*
                          </label>
                          <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={handleUserSelect}
                            style={{ minWidth: "120px" }}
                            disabled={!!state?.employee || !!state}
                          >
                            {state?.employee || state ? 'User Pre-selected' : 'Select User'}
                          </button> 
                        </div>
                      </div>
                      
                      {/* Show role type dropdown in both modes since it exists in your data */}
                      {/* <div className="col-lg-6 col-md-12">
                        <div className="mb-3">
                          <label htmlFor="roleType" className="form-label">
                            Role Type*
                          </label>
                          <select
                            id="roleType"
                            className="form-select"
                            value={formData.roleType}
                            onChange={(e) => setFormData({...formData, roleType: e.target.value})}
                          >
                            <option value="Admin">Admin</option>
                            <option value="User">User</option>
                            <option value="Admin">Employee</option>
                            <option value="User">Viewer</option>
                          </select>
                        </div>
                      </div> */}
                    </div>

                    <div className="col-12">
                      {(formData.selectedUser || state?.employee || state?.userId) && (
                        <div className="card mb-3">
                          <div className="card-header">
                            <h6 className="card-title mb-0">Selected User Details</h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-sm-6 col-12 mb-2">
                                <strong>Employee Id:</strong> {(formData.selectedUser || state?.employee || state?.userId)?.employeeId || 'N/A'}
                              </div>
                              <div className="col-sm-6 col-12 mb-2">
                                <strong>Name:</strong> {(formData.selectedUser || state?.employee || state?.userId)?.username || 'N/A'}
                              </div>
                              <div className="col-sm-6 col-12 mb-2">
                                <strong>Email:</strong> {(formData.selectedUser || state?.employee || state?.userId)?.email || 'N/A'}
                              </div>
                              <div className="col-sm-6 col-12 mb-2">
                                <strong>Designation:</strong> {(formData.selectedUser || state?.employee || state?.userId)?.designation || 'N/A'}
                              </div>
                              <div className="col-sm-6 col-12 mb-2">
                                <strong>Phone:</strong> {(formData.selectedUser || state?.employee || state?.userId)?.contactNumber || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="row mt-4">
                      <div className="col-12">
                        <div className="card-header mb-3">
                          <h5 className="card-title mb-0">Permissions Configuration</h5>
                          <small className="text-muted">Select actions and specify allowed IP addresses for each permission</small>
                        </div>
                        
                        <div className="row">
                          {permissions.map((permission) => (
                            <div key={permission.permissionName} className="col-12 mb-4">
                              <div className={`card permission-card ${hasActionsSelected(permission.permissionName) ? 'border-primary' : ''}`}>
                                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0">{permissionDisplayNames[permission.permissionName]}</h6>
                                  {hasActionsSelected(permission.permissionName) && (
                                    <span className="badge bg-primary">Active</span>
                                  )}
                                </div>
                                <div className="card-body">
                                  {/* Actions Section */}
                                  <div className="row mb-3">
                                    <div className="col-12">
                                      <label className="form-label fw-semibold">Actions:</label>
                                      <div className="row">
                                        {['read', 'write', 'update', 'delete'].map(action => (
                                          <div key={action} className="col-md-3 col-6 mb-2">
                                            <div className="form-check">
                                              <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id={`${permission.permissionName}-${action}`}
                                                checked={permission.actions.includes(action)}
                                                onChange={() => updatePermissionActions(permission.permissionName, action)}
                                              />
                                              <label className="form-check-label text-capitalize" htmlFor={`${permission.permissionName}-${action}`}>
                                                {action}
                                              </label>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>

                                  {/* IP Addresses Section */}
                                  <div className="row">
                                    <div className="col-12">
                                      <label className="form-label fw-semibold">Allowed IP Addresses:</label>
                                      <div className="d-flex align-items-center gap-3 mb-2">
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary btn-sm"
                                          onClick={() => handleIpSelect(permission.permissionName)}
                                        >
                                          Add IP Address
                                        </button>
                                        <span className="text-muted small">
                                          {getIpDisplayText(permission.allowedIPs)}
                                        </span>
                                      </div>
                                      
                                      {/* Display selected IPs with remove buttons */}
                                      {permission.allowedIPs.filter(ip => ip.trim() !== "").length > 0 && (
                                        <div className="mt-2">
                                          <div className="selected-ips-container">
                                            {permission.allowedIPs.filter(ip => ip.trim() !== "").map((ip, index) => (
                                              <div key={index} className="d-flex align-items-center gap-2 mb-1">
                                                <span className="badge bg-secondary">{ip}</span>
                                                <button
                                                  type="button"
                                                  className="btn btn-outline-danger btn-sm"
                                                  onClick={() => removeIpAddress(permission.permissionName, ip)}
                                                  title="Remove IP"
                                                >
                                                  ×
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      <small className="text-muted d-block mt-2">
                                        Click "Add IP Address" multiple times to add multiple IPs. Use '*' for all IPs.
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        // disabled={loading || !formData.userId}
                        style={{ minWidth: "120px" }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            {isEditMode ? 'Updating...' : 'Saving...'}
                          </>
                        ) : (
                          isEditMode ? 'Update Permissions' : 'Save Permissions'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {showUserModal && (
        <RoleModel 
          onClose={() => setShowUserModal(false)}
          onSelect={(user) => {
            setFormData({...formData, userId: user._id || user.id, selectedUser: user});
            setShowUserModal(false);
          }}
        />
      )}
      
      <IpAddressModal
        show={showIpModal}
        onClose={() => setShowIpModal(false)}
        onSelect={handleSelectIps}
        initialIps={[]}
      />
    </div>
  );
}